import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const AIResultsCompareModal = ({ 
  isOpen, 
  aiResults, 
  initialImageType,
  backgroundRemovedImageWithColor,
  onClose 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [images, setImages] = useState([]);

  // 이미지 리스트 생성
  useEffect(() => {
    if (!aiResults) {
      setImages([]);
      return;
    }

    const imageList = [];

    // 원본 이미지
    if (aiResults.originalFile) {
      imageList.push({
        type: 'original',
        file: aiResults.originalFile,
        url: URL.createObjectURL(aiResults.originalFile),
        name: '원본 사진'
      });
    }

    // AI 화질개선 이미지
    if (aiResults.gfpganFile) {
      imageList.push({
        type: 'gfpgan',
        file: aiResults.gfpganFile,
        url: URL.createObjectURL(aiResults.gfpganFile),
        name: 'AI 화질개선'
      });
    }

    // AI 배경제거 이미지
    if (aiResults.gfpganBackgroundRemoverFile) {
      const displayUrl = backgroundRemovedImageWithColor ? 
        URL.createObjectURL(backgroundRemovedImageWithColor) : 
        URL.createObjectURL(aiResults.gfpganBackgroundRemoverFile);
      
      imageList.push({
        type: 'gfpgan-bg-remover',
        file: backgroundRemovedImageWithColor || aiResults.gfpganBackgroundRemoverFile,
        url: displayUrl,
        name: 'AI 배경제거'
      });
    }

    // AI 흑백효과 이미지
    if (aiResults.gfpganBackgroundRemoverBlackWhiteFile) {
      imageList.push({
        type: 'gfpgan-bg-remover-bw',
        file: aiResults.gfpganBackgroundRemoverBlackWhiteFile,
        url: URL.createObjectURL(aiResults.gfpganBackgroundRemoverBlackWhiteFile),
        name: 'AI 흑백효과'
      });
    }

    setImages(imageList);

    // 초기 인덱스 설정
    if (initialImageType && imageList.length > 0) {
      const initialIndex = imageList.findIndex(img => img.type === initialImageType);
      setCurrentIndex(initialIndex >= 0 ? initialIndex : 0);
    } else {
      setCurrentIndex(0);
    }
  }, [aiResults, backgroundRemovedImageWithColor, initialImageType]);

  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback((event) => {
    if (!isOpen || images.length === 0) return;

    switch (event.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        setCurrentIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        setCurrentIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
        break;
    }
  }, [isOpen, images.length, onClose]);

  // 키보드 이벤트 등록
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  // 터치/드래그 이벤트를 위한 상태
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // 최소 스와이프 거리
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // 왼쪽으로 스와이프 = 다음 이미지
      setCurrentIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
    } else if (isRightSwipe) {
      // 오른쪽으로 스와이프 = 이전 이미지
      setCurrentIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : images.length - 1);
  };

  const goToNext = () => {
    setCurrentIndex(prev => prev < images.length - 1 ? prev + 1 : 0);
  };

  if (!isOpen || !aiResults || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div 
      className="modal-overlay"
      onClick={onClose}
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 70 }}
    >
      <div className="relative w-full h-full max-w-6xl flex flex-col items-center justify-center">
        {/* 닫기 버튼 */}
        <button 
          onClick={onClose}
          className="modal-close-btn absolute top-4 right-4 z-10"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}
          title="닫기"
        >
          <X className="w-5 h-5" />
        </button>

        {/* 이전 버튼 */}
        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="btn btn-ghost absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: '9999px', padding: '0.75rem' }}
            title="이전 이미지"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        )}

        {/* 다음 버튼 */}
        {images.length > 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="btn btn-ghost absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: '9999px', padding: '0.75rem' }}
            title="다음 이미지"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        )}

        {/* 메인 이미지 */}
        <div 
          className="flex-1 flex items-center justify-center w-full"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <img 
            src={currentImage.url} 
            alt={currentImage.name}
            className="max-w-full max-h-full object-contain transition-opacity duration-300"
            onClick={(e) => e.stopPropagation()}
            style={{ maxHeight: 'calc(100vh - 200px)' }}
          />
        </div>

        {/* 하단 정보 및 네비게이션 */}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-4">
          <div className="max-w-6xl mx-auto">
            {/* 이미지 정보 */}
            <div className="text-center mb-4">
              <h3 className="text-xl font-semibold mb-1">{currentImage.name}</h3>
              <p className="text-sm text-gray-300">
                {currentIndex + 1} / {images.length} - 좌우 화살표키나 스와이프로 이동
              </p>
            </div>

            {/* 썸네일 네비게이션 */}
            {images.length > 1 && (
              <div className="flex justify-center gap-2 overflow-x-auto pb-2">
                {images.map((image, index) => (
                  <button
                    key={image.type}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                    }}
                    className={`flex-shrink-0 relative ${
                      index === currentIndex 
                        ? 'ring-2 ring-blue-400' 
                        : 'ring-1 ring-gray-600 hover:ring-gray-400'
                    } rounded transition-all duration-200`}
                  >
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white text-xs p-1 rounded-b text-center truncate">
                      {image.name}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* 키보드 안내 */}
            <div className="text-center mt-3 text-xs text-gray-400">
              ESC: 닫기 | ← →: 이미지 이동 | 바깥 영역 클릭: 닫기
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIResultsCompareModal;
